import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';

import { useOidcIdToken } from '@axa-fr/react-oidc';

import { allPermissions, Permission } from '@repo/api/auth';
import { userQueries, useUpdateLastLoginMutate } from '@repo/api/users';

import { PermissionOverrides, User, UserContext } from './user';
import { OidcIdToken, OidcIdTokenPayload } from './user.types';

const useTokens = () => {
	const { idTokenPayload: idToken } = useOidcIdToken() as OidcIdToken;

	return {
		idToken,
	};
};

const useLastLoginSync = (subjectId?: string) => {
	const { mutateAsync: updateLastLogin } = useUpdateLastLoginMutate();

	useEffect(() => {
		if (!subjectId) {
			return;
		}

		void updateLastLogin(subjectId);
	}, [updateLastLogin, subjectId]);
};

const useOverridable = (apiPermissions: Permission[]) => {
	const [enabled, setEnabled] = useState(location.hostname === 'localhost');

	const [overrides, setOverrides] = useState<PermissionOverrides>({});

	const overridePermission = useCallback(
		function (name: Permission) {
			if (!enabled) return;
			setOverrides((prev) => ({
				...prev,
				[name]:
					apiPermissions.includes(name) ?
						prev[name] === false ?
							null
						:	false
					: prev[name] === true ? null
					: true,
			}));
		},
		[enabled, apiPermissions],
	);

	return useMemo(() => {
		function doOverride(permissions: Permission[]) {
			if (!enabled) return permissions;
			return allPermissions.reduce<Permission[]>((a, c) => {
				if (
					(permissions.includes(c) && overrides[c] !== false) ||
					(!permissions.includes(c) && overrides[c] === true)
				) {
					a.push(c);
				}
				return a;
			}, []);
		}

		const overriddenPermissions = doOverride(apiPermissions);

		type Permissions = User['permissions'];

		return Object.assign(overriddenPermissions, {
			override: overridePermission,
			overrides: enabled ? overrides : {},
			overridesActive: enabled,
			reset: () => {
				setOverrides({});
			},
			toggleActive: () => {
				setEnabled(!enabled);
			},
		}) satisfies Permissions;
	}, [apiPermissions, enabled, overridePermission, overrides]);
};

export const UserProvider = ({
	children,
	logoutMessage,
}: {
	children: ReactNode;
	logoutMessage: ReactNode;
}) => {
	const { idToken } = useTokens();

	if (!idToken) {
		return logoutMessage;
	}

	return (
		<UserOverridePermissonsProvider idToken={idToken}>
			{children}
		</UserOverridePermissonsProvider>
	);
};

const UserOverridePermissonsProvider = ({
	idToken,
	children,
}: {
	idToken: OidcIdTokenPayload;
	children: ReactNode;
}) => {
	const { data: user } = useSuspenseQuery(userQueries.id(idToken.sub));
	useLastLoginSync(idToken.sub);

	const permissions = useOverridable(user.permissions ?? []);

	const userContext = useMemo(
		() => ({
			...user,
			permissions,
		}),
		[user, permissions],
	);

	return <UserContext value={userContext}>{children}</UserContext>;
};
